<script>
  import { onMount } from "svelte";
  import { DataTable } from "carbon-components-svelte";
  import Modal from "lib/components/Modal.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";

  const headers = [
    { key: "container", value: "Container" },
    { key: "status", value: "Status" },
    { key: "message", value: "Message" },
    { key: "lastErrorDate", value: "Last error date" },
    { key: "log", value: "Log" },
  ];
  const containers = [
    "analysis-backend", "authentication-backend", "damprojects-backend",
    "frontend", "knextractor-backend", "monitoring-backend",
    "processmodeler-backend", "recorderservice-backend",
  ];
  let rows = containers.map((container) => ({
    id: container,
    container,
    status: "Not checked",
    lastErrorDate: "–",
    message: "Status is being loaded...",
  }));
  let error = "";
  let logViewerOpen = false;
  let logLoading = false;
  let logError = "";
  let logContent = "";

  onMount(loadContainerStatus);

  function formatLastErrorDate(value) {
    if (!value) return "–";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("de-DE");
  }

  async function loadContainerStatus() {
    error = "";
    try {
      const [response, readinessResponse] = await Promise.all([
        fetchWithSession("/api/damprojects/admin/container-status"),
        fetchWithSession("/api/damprojects/admin/health/ready"),
      ]);
      const data = await response.json();
      const readiness = await readinessResponse.json();
      if (!response.ok) throw new Error(data.message || `Container status could not be loaded (${response.status}).`);
      const statusByContainer = new Map((data.services || []).map((service) => [service.container, service]));
      rows = rows.map((row) => {
        const service = statusByContainer.get(row.container);
        if (!service) {
          return {
            ...row,
            status: "Unknown",
            message: "Service was not returned by the status endpoint.",
          };
        }
        if (row.container === "damprojects-backend" && readinessResponse.status === 503) {
          return {
            ...row,
            status: "unhealthy",
            lastErrorDate: formatLastErrorDate(service.last_error_date),
            message: "Service is reachable, but the database is not ready.",
          };
        }
        return {
          ...row,
          status: service.status,
          lastErrorDate: formatLastErrorDate(service.last_error_date),
          message: row.container === "damprojects-backend" && readinessResponse.ok && readiness.status === "ready"
            ? "Service is reachable and the database is ready."
            : service.reachable
            ? `Service is reachable on port ${service.checked_port}.`
            : "Container is not reachable.",
        };
      });
    } catch (loadError) {
      error = loadError.message || "Container status could not be loaded.";
      rows = rows.map((row) => ({ ...row, status: "Unknown", message: error }));
    }
  }

  async function viewErrors() {
    logViewerOpen = true;
    logLoading = true;
    logError = "";
    try {
      const response = await fetchWithSession("/api/damprojects/admin/server-logs/damprojects-backend");
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Error log could not be loaded.");
      logContent = data.content || "No errors recorded.";
    } catch (loadError) {
      logError = loadError.message || "Error log could not be loaded.";
    } finally {
      logLoading = false;
    }
  }

</script>

<div class="server-log-table">
  {#if error}<p class="error">{error}</p>{/if}
  <DataTable headers={headers} {rows} class="custom-table">
    <svelte:fragment slot="cell" let:row let:cell>
      {#if cell.key === "status"}
        <span class:running={row.status === "running"} class="status-pill">
          {row.status}
        </span>
      {:else if cell.key === "log"}
        {#if row.container === "damprojects-backend"}
          <div class="actions-cell">
            <button type="button" on:click={viewErrors}>View</button>
          </div>
        {:else}
          –
        {/if}
      {:else}
        {cell?.value || "-"}
      {/if}
    </svelte:fragment>
  </DataTable>
</div>

<Modal bind:open={logViewerOpen} hideFooter modalHeading="damprojects-backend errors" on:close={() => (logViewerOpen = false)}>
  {#if logLoading}
    <p>Loading errors...</p>
  {:else if logError}
    <p class="error">{logError}</p>
  {:else}
    <pre class="log-content">{logContent}</pre>
  {/if}
</Modal>

<style>
  .server-log-table { overflow-x: auto; padding: 0 0 0.75rem; }
  .error { margin: 0 0 0.75rem; color: #da1e28; }
  .status-pill { display: inline-flex; align-items: center; padding: 0.25rem 0.5rem; border-radius: 2rem; background: #fff1f1; color: #a2191f; font-size: 0.8rem; text-transform: capitalize; white-space: nowrap; }
  .status-pill.running { background: #defbe6; color: #0e6027; }
  .actions-cell { display: flex; gap: var(--control-gap); }
  .actions-cell button { padding: 0.4rem 0.65rem; border: 0; border-radius: 0.35rem; background: var(--pax-black); color: var(--pax-white); cursor: pointer; }
  .log-content { max-height: 24rem; max-width: 48rem; overflow: auto; margin: 0; padding: 0.75rem; background: #161616; color: #f4f4f4; white-space: pre-wrap; word-break: break-word; font: 0.75rem/1.4 monospace; }
  :global(.custom-table) { min-width: 42rem; }
  :global(.custom-table th:last-child), :global(.custom-table td:last-child) { width: 1%; white-space: nowrap; text-align: left !important; }
  :global(.custom-table th:last-child .bx--table-header-label) { justify-content: flex-start; text-align: left; }
</style>
